// announcerRunStore — хранилище состояния текущего запуска (батча) двойной
// генерации, ЖИВУЩЕЕ ВНЕ дерева компонентов (правка итогового ревью r4 №1).
//
// ЗАЧЕМ: раньше состояние запуска (варианты, фазы, что показывать) жило в
// `useState` самой SocialAnnouncerPage. Переключатель «Генерация» / «История»
// в ToolLayout — соседний дочерний маршрут ТОГО ЖЕ инструмента (react-router
// Outlet), и переход на «Историю» полностью РАЗМОНТИРУЕТ SocialAnnouncerPage.
// При возврате `useState` создавался заново — кнопка «Сгенерировать»
// оказывалась активна, хотя варианты ещё генерировались (риск повторного
// платного запуска), окно с результатами терялось, а упавший вариант было
// нечем повторить (в Историю попадают только succeeded-записи).
//
// Модуль-синглтон переживает размонтирование/монтирование страницы в пределах
// ОДНОЙ вкладки браузера (SPA, JS-модуль не перезагружается при client-side
// навигации) и торчит намеренно недолго: полная перезагрузка страницы (F5)
// обнуляет его — это ПРИНЯТОЕ ограничение (05_implementation_spec.md §8.6,
// «Известные ограничения»), которое эта правка не трогает.
//
// ЗАЩИТА ОТ ЧУЖОГО ЗАПУСКА: в состоянии хранится `userId` — id пользователя
// НА МОМЕНТ ЗАПУСКА. Если текущий пользователь на странице (см. useMe(),
// frontend/src/hooks/useAuth.ts) отличается от `userId` запуска или ещё не
// определён — SocialAnnouncerPage считает, что запуска нет, и чистит
// хранилище (см. SocialAnnouncerPage.tsx, isRunOwnedByUser). Сценарий: логаут
// и логин другим пользователем в той же вкладке без полной перезагрузки —
// без этой проверки второй пользователь увидел бы (и мог бы повторить/
// закрыть) чужой незавершённый запуск.
import { useSyncExternalStore } from "react";
import type { AnnouncerVariant } from "@/api/announcer";
import type { TextVariantPhase } from "@/components/results/TextVariantResultBlock";

/** Состояние одного запуска (батча): актуальные варианты (после повтора — с
 *  новыми generation_id), фаза каждого по generation_id (отсутствие ключа
 *  трактуется как "pending" — новый вариант ещё не успел прислать первую
 *  фазу) и id пользователя, запустившего генерацию. */
export interface AnnouncerRun {
  variants: AnnouncerVariant[];
  phases: Record<string, TextVariantPhase>;
  /** id пользователя (MeResponse.id) на момент запуска. null — пользователь
   *  на момент запуска не был определён (защитный случай, на практике не
   *  должен встречаться: отправка формы возможна только за ProtectedRoute). */
  userId: string | null;
  /** id варианта (упавшего/зависшего), для которого клик «Повторить» УЖЕ
   *  ушёл на сервер, но ответ ещё не пришёл — правка F4 №2. Отдельно от
   *  `phases`, а не запись `phases[oldId] = "pending"` — НАРОЧНО: блок со
   *  СТАРЫМ generation_id продолжает жить (сервер ещё не подтвердил повтор,
   *  вариант формально не заменён) и, если он перемонтировался без кэша
   *  (переход в «Историю» и обратно, пока повтор в пути), сам честно
   *  репортит странице СВОЙ настоящий текущий статус (например, всё ещё
   *  "failed") — запись прямо в `phases[oldId]` тогда бы гонялась с этими
   *  репортами и то и дело откатывалась обратно. Необязательное поле —
   *  старые запуски (до этой правки) читаются как «повторов в пути нет». */
  retryingIds?: Record<string, true>;
}

// Итоговые фазы варианта: готов, отказ, завис (серверный возраст варианта ≥
// порога staleRetryAfterS — НЕЗАВИСИМО от того, идёт ли опрос сейчас: правка
// r5 №2 итогового ревью убрала условие «опрос остановлен» из isStale, см.
// TextVariantResultBlock.tsx). «В работе» — только pending. Вынесено сюда
// (а не в SocialAnnouncerPage.tsx): и хранилищу, и странице нужно одно и то
// же понятие «запуск завершён».
export const ANNOUNCER_FINAL_PHASES: ReadonlySet<TextVariantPhase> = new Set([
  "succeeded",
  "failed",
  "gave_up",
]);

/** Хотя бы один вариант запуска ещё не дошёл до итоговой фазы — ИЛИ для
 *  какого-то варианта прямо сейчас в пути ответ сервера на «Повторить»
 *  (retryingIds, правка F4 №2): кнопка «Сгенерировать» обязана оставаться
 *  заблокированной уже в момент клика «Повторить», не дожидаясь ответа. */
export function isAnnouncerRunInProgress(run: AnnouncerRun | null): boolean {
  return (
    !!run &&
    (run.variants.some((v) => !ANNOUNCER_FINAL_PHASES.has(run.phases[v.generation_id] ?? "pending")) ||
      Object.keys(run.retryingIds ?? {}).length > 0)
  );
}

/** Все варианты запуска дошли до succeeded (ни одного отказа/«зависшего»). */
export function isAnnouncerRunAllSucceeded(run: AnnouncerRun | null): boolean {
  return !!run && run.variants.every((v) => run.phases[v.generation_id] === "succeeded");
}

let current: AnnouncerRun | null = null;
const listeners = new Set<() => void>();

/** Текущее состояние запуска — синхронное чтение вне рендера (обработчики,
 *  эффекты). Внутри рендера предпочитать хук useAnnouncerRun(). */
export function getAnnouncerRun(): AnnouncerRun | null {
  return current;
}

/** Обновить запуск — значением или функцией-обновлятором (как setState).
 *  Уведомляет подписчиков ТОЛЬКО если значение реально поменялось (важно:
 *  useSyncExternalStore предупреждает о бесконечном цикле, если getSnapshot
 *  возвращает новый объект при каждом вызове, поэтому вызывающий код обязан
 *  возвращать прежнюю ссылку, когда менять нечего — см. handlePhaseChange). */
export function setAnnouncerRun(
  next: AnnouncerRun | null | ((prev: AnnouncerRun | null) => AnnouncerRun | null),
): void {
  const value = typeof next === "function" ? next(current) : next;
  if (value === current) return;
  current = value;
  listeners.forEach((listener) => listener());
}

/** Подписка на изменения — для useSyncExternalStore. Возвращает функцию отписки. */
export function subscribeAnnouncerRun(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Реактивное чтение запуска в компоненте. SSR здесь нет (обычное SPA,
 *  ReactDOM.createRoot) — третий аргумент useSyncExternalStore не нужен. */
export function useAnnouncerRun(): AnnouncerRun | null {
  return useSyncExternalStore(subscribeAnnouncerRun, getAnnouncerRun);
}

/** ТОЛЬКО для тестов: сбросить хранилище между кейсами. Модуль — синглтон, он
 *  живёт дольше одного теста/рендера, поэтому без явного сброса в beforeEach
 *  тесты становятся зависимыми друг от друга (запуск из одного кейса «утекает»
 *  в следующий). Импортировать только из *.test.tsx. */
export function resetAnnouncerRunStoreForTests(): void {
  current = null;
}
